import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { createSale, getByBarcode, getCategories, getProducts, getSettings, printJob } from '../utils/api';
import { loadHtml5Qrcode } from '../utils/html5Qrcode';
import { useCartStore } from '../store';
import { fmt } from '../utils/format';
import { openReceiptPreview } from '../utils/receipt';
import ProductThumb from '../components/ProductThumb';

const SCANNER_REGION_ID = 'camera-barcode-reader';
const SUPPORTED_BARCODE_FORMATS = [
  'CODE_128',
  'CODE_39',
  'CODE_93',
  'CODABAR',
  'ITF',
  'EAN_13',
  'EAN_8',
  'UPC_A',
  'UPC_E',
  'UPC_EAN_EXTENSION',
  'RSS_14',
  'RSS_EXPANDED',
  'PDF_417',
  'QR_CODE',
];

function formatScannerError(error) {
  const message = String(error?.message || error || '').trim();
  const lower = message.toLowerCase();

  if (!message) return 'Unable to start barcode scanner';
  if (lower.includes('permission') || lower.includes('notallowederror')) return 'Camera permission was denied. Allow camera access and try again.';
  if (lower.includes('secure context') || lower.includes('insecure')) return 'Camera scanning requires HTTPS or localhost in this browser.';
  if (lower.includes('no camera') || lower.includes('notfounderror')) return 'No camera was found on this device.';
  if (lower.includes('notreadableerror') || lower.includes('trackstarterror')) return 'The camera is busy in another app or tab. Close other apps using the camera and try again.';
  if (lower.includes('cannot stop')) return 'The scanner had already stopped. Please open it again if you still want to scan.';
  if (lower.includes('barcode scanner library failed to load')) return 'The barcode scanner could not load. Refresh the page and try again.';
  return message;
}

function CameraScannerModal({ open, onClose, onDetected }) {
  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    let scanner = null;
    let scannerState = null;
    let startResolved = false;

    async function safeCleanup() {
      if (!scanner) return;

      try {
        const state = typeof scanner.getState === 'function' ? scanner.getState() : null;
        const canStop = state === scannerState?.SCANNING || state === scannerState?.PAUSED;
        if (canStop) await scanner.stop();
      } catch (cleanupError) {
        const message = String(cleanupError?.message || cleanupError || '');
        if (!message.toLowerCase().includes('cannot stop')) {
          console.warn('Scanner cleanup warning:', cleanupError);
        }
      }

      try {
        scanner.clear();
      } catch (clearError) {
        console.warn('Scanner clear warning:', clearError);
      }
    }

    async function startScanner() {
      try {
        const scannerLib = await loadHtml5Qrcode();
        if (cancelled) return;

        const { Html5Qrcode, Html5QrcodeSupportedFormats, Html5QrcodeScannerState } = scannerLib;
        scannerState = Html5QrcodeScannerState;
        scanner = new Html5Qrcode(SCANNER_REGION_ID);

        const cameras = await Html5Qrcode.getCameras();
        if (!cameras.length) throw new Error('No camera found on this device');
        if (cancelled) {
          await safeCleanup();
          return;
        }

        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 12,
            disableFlip: false,
            aspectRatio: 1.777778,
            qrbox: { width: 300, height: 170 },
            formatsToSupport: SUPPORTED_BARCODE_FORMATS.map((key) => Html5QrcodeSupportedFormats[key]).filter((value) => value !== undefined),
          },
          (decodedText) => {
            if (cancelled) return;
            onDetected(decodedText.trim());
            onClose();
          },
          () => {}
        );

        startResolved = true;
        if (cancelled) await safeCleanup();
      } catch (error) {
        if (cancelled) return;

        console.warn('Scanner start failed:', error);
        toast.error(formatScannerError(error));

        if (scanner && !startResolved) await safeCleanup();
        onClose();
      }
    }

    startScanner();

    return () => {
      cancelled = true;
      safeCleanup();
    };
  }, [open, onClose, onDetected]);

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal modal-sm scale-in">
        <div className="modal-head">
          <div className="modal-title">Scan Product Barcode</div>
          <button className="modal-close" onClick={onClose}>x</button>
        </div>
        <div className="modal-body">
          <div id={SCANNER_REGION_ID} style={{ minHeight: 320, borderRadius: 16, overflow: 'hidden', background: '#000' }} />
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--ink3)' }}>
            Supports common retail and packaging barcodes including EAN-13, EAN-8, UPC, Code 128, Code 39, ITF, ISBN-style book barcodes, and more.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function POSPage() {
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('All');
  const [showReceipt, setShowReceipt] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const searchRef = useRef(null);
  const qc = useQueryClient();

  const { items, addItem, removeItem, updateQty, setDiscount, discountPct, paymentMethod, setPaymentMethod, mpesaRef, setMpesaRef, clear, totals } = useCartStore();

  const { data: catData } = useQuery({ queryKey: ['categories'], queryFn: () => getCategories().then((response) => response.data) });
  const { data: prodData, isLoading } = useQuery({
    queryKey: ['products', { search, category: activeCat === 'All' ? undefined : activeCat }],
    queryFn: () => getProducts({ search, category: activeCat === 'All' ? undefined : activeCat, limit: 200 }).then((response) => response.data),
    keepPreviousData: true,
  });
  const { data: settings = {} } = useQuery({ queryKey: ['settings'], queryFn: () => getSettings().then((response) => response.data) });

  const products = prodData?.products || [];
  const categories = ['All', ...(catData?.map((category) => category.name) || [])];
  const { subtotal, discountAmt, taxAmt, total, itemCount } = totals();

  const barcodeBuffer = useRef('');
  const barcodeTimer = useRef(null);

  useEffect(() => {
    function onKey(event) {
      if (document.activeElement?.tagName === 'INPUT' && document.activeElement !== searchRef.current) return;
      if (event.key === 'Enter' && barcodeBuffer.current.length > 4) {
        handleBarcode(barcodeBuffer.current);
        barcodeBuffer.current = '';
        return;
      }
      if (event.key.length === 1) {
        barcodeBuffer.current += event.key;
        clearTimeout(barcodeTimer.current);
        barcodeTimer.current = setTimeout(() => {
          barcodeBuffer.current = '';
        }, 100);
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  async function handleBarcode(code) {
    try {
      const normalizedCode = String(code || '').trim();
      if (!normalizedCode) {
        toast.error('Scanned barcode was empty');
        return;
      }

      const { data: product } = await getByBarcode(normalizedCode);
      const added = addItem(product);
      if (!added) toast.error(`Max stock reached for ${product.name}`);
      else toast.success(`${product.name} scanned`);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error(`Product does not exist for barcode: ${code}`);
      } else if (err?.message === 'Network Error') {
        toast.error('Cannot reach the server while scanning');
      } else {
        toast.error(err?.response?.data?.error || 'Barcode scan failed');
      }
    }
  }

  const saleMutation = useMutation({
    mutationFn: (payload) => createSale(payload).then((response) => response.data),
    onSuccess: (sale) => {
      setShowReceipt(sale);
      setShowPayModal(false);
      clear();
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['summary'] });
      qc.invalidateQueries({ queryKey: ['sales'] });
    },
    onError: (err) => toast.error(err?.response?.data?.error || 'Sale failed'),
  });

  const printMutation = useMutation({
    mutationFn: (sale) => printJob('receipt', { ...sale, settingsSnapshot: settings }, navigator.userAgent.slice(0, 80)),
    onSuccess: () => toast.success('Receipt queued for printing'),
    onError: (err) => toast.error(err?.response?.data?.error || 'Unable to queue receipt print'),
  });

  function handlePay() {
    if (!items.length) return;
    saleMutation.mutate({
      items: items.map((item) => ({ product_id: item.id, qty: item.qty })),
      discount_pct: discountPct,
      payment_method: paymentMethod,
      mpesa_ref: mpesaRef || undefined,
      device_id: navigator.userAgent.slice(0, 80),
    });
  }

  function previewReceipt() {
    if (!showReceipt) return;
    openReceiptPreview(showReceipt, settings);
  }

  return (
    <div className="pos-layout">
      <div className="pos-left">
        <div className="search-bar mb-8">
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input ref={searchRef} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search products or scan barcode..." autoFocus />
          {search && <button onClick={() => setSearch('')} style={{ color: 'var(--ink3)', padding: '0 4px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>x</button>}
          <button className="btn btn-secondary btn-sm" style={{ marginRight: 6 }} onClick={() => setScannerOpen(true)}>Scan</button>
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCat(category)}
              style={{
                padding: '5px 13px',
                borderRadius: 99,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all .14s',
                border: activeCat === category ? '1.5px solid var(--brand)' : '1.5px solid var(--border)',
                background: activeCat === category ? 'var(--brand)' : 'var(--surface)',
                color: activeCat === category ? '#fff' : 'var(--ink3)',
                fontFamily: 'inherit',
                userSelect: 'none',
              }}
            >
              {category}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}><div className="spinner" /></div>
        ) : products.length === 0 ? (
          <div className="empty-state"><div className="empty-title">No products found</div><div className="empty-sub">Try a different search or scan a barcode.</div></div>
        ) : (
          <div className="product-grid">
            {products.map((product) => (
              <div
                key={product.id}
                className={`product-tile ${product.stock === 0 ? 'out-of-stock' : ''}`}
                onClick={() => {
                  if (product.stock === 0) return;
                  const added = addItem(product);
                  if (!added) toast.error('Max stock reached');
                  else toast.success(`${product.name} added`, { duration: 1000 });
                }}
              >
                <ProductThumb product={product} size="lg" className="product-tile-thumb" />
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)', marginBottom: 3, lineHeight: 1.3 }}>{product.name}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand)', fontFamily: 'var(--mono)' }}>{fmt.currency(product.price)}</div>
                <div style={{ fontSize: 10.5, color: product.stock <= product.low_stock_at ? 'var(--amber)' : 'var(--ink4)', marginTop: 2 }}>
                  {product.stock === 0 ? 'Out of stock' : product.stock <= product.low_stock_at ? `Low: ${product.stock}` : `${product.stock} in stock`}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="pos-right">
        <div className="cart-panel pos-priority-panel" style={{ flex: 1 }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Current sale</div>
            <span className="badge badge-gray">{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
          </div>

          <div className="cart-items-scroll" style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
            {items.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 8, color: 'var(--ink4)', padding: 24, textAlign: 'center' }}>
                <div className="empty-title">Cart is empty</div>
                <div style={{ fontSize: 13 }}>Scan first, then tap products below for fast checkout.</div>
              </div>
            ) : items.map((item) => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', borderRadius: 'var(--r-sm)', transition: 'background .1s' }}>
                <ProductThumb product={item} size="sm" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--ink3)', fontFamily: 'var(--mono)' }}>{fmt.currency(item.price)} each</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <QtyBtn onClick={() => updateQty(item.id, item.qty - 1)}>-</QtyBtn>
                  <span style={{ fontSize: 12, fontFamily: 'var(--mono)', minWidth: 20, textAlign: 'center' }}>{item.qty}</span>
                  <QtyBtn onClick={() => updateQty(item.id, item.qty + 1)} disabled={item.qty >= item.stock}>+</QtyBtn>
                </div>
                <div style={{ fontSize: 12.5, fontFamily: 'var(--mono)', color: 'var(--ink2)', minWidth: 80, textAlign: 'right' }}>{fmt.currency(item.price * item.qty)}</div>
                <button onClick={() => removeItem(item.id)} style={{ color: 'var(--ink4)', cursor: 'pointer', fontSize: 14, background: 'none', border: 'none', flexShrink: 0, padding: '2px 4px' }}>x</button>
              </div>
            ))}
          </div>

          <div style={{ padding: '14px 16px', borderTop: '1px solid var(--border)' }}>
            <div className="grid-2 mb-12 pos-quick-actions">
              <button className="btn btn-secondary" onClick={() => setScannerOpen(true)}>Scan Product</button>
              <button className="btn btn-secondary" onClick={clear}>Clear Sale</button>
            </div>
            <TotalRow label="Subtotal" value={fmt.currency(subtotal)} />
            <TotalRow label="VAT (16%)" value={fmt.currency(taxAmt)} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 7, color: 'var(--ink2)', alignItems: 'center' }}>
              <span>Discount</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="number" min="0" max="100" value={discountPct} onChange={(event) => setDiscount(Number(event.target.value))} style={{ width: 44, padding: '2px 6px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, textAlign: 'right', color: 'var(--ink)', fontFamily: 'var(--mono)', outline: 'none' }} />
                <span style={{ fontSize: 12, color: 'var(--ink3)' }}>%</span>
              </span>
            </div>
            {discountAmt > 0 && <TotalRow label="Discount value" value={`- ${fmt.currency(discountAmt)}`} color="var(--green)" />}
            <div style={{ height: 1, background: 'var(--border)', margin: '10px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 700, color: 'var(--ink)' }}>
              <span>TOTAL</span>
              <span style={{ fontFamily: 'var(--mono)' }}>{fmt.currency(total)}</span>
            </div>
            <button className="btn btn-primary" disabled={items.length === 0 || saleMutation.isPending} style={{ width: '100%', justifyContent: 'center', padding: 13, fontSize: 14, marginTop: 10, boxShadow: '0 2px 8px rgba(26,86,219,.3)' }} onClick={() => setShowPayModal(true)}>
              {saleMutation.isPending ? <><div className="spinner spinner-sm" style={{ borderTopColor: '#fff' }} />Processing...</> : 'Process Payment'}
            </button>
          </div>
        </div>
      </div>

      {showPayModal && (
        <div className="modal-overlay" onClick={(event) => event.target === event.currentTarget && setShowPayModal(false)}>
          <div className="modal modal-sm scale-in">
            <div className="modal-head">
              <div className="modal-title">Choose Payment Method</div>
              <button className="modal-close" onClick={() => setShowPayModal(false)}>x</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                {[
                  { id: 'cash', label: 'Cash' },
                  { id: 'mpesa', label: 'M-Pesa' },
                  { id: 'card', label: 'Card' },
                  { id: 'credit', label: 'Credit' },
                ].map((method) => (
                  <button key={method.id} onClick={() => setPaymentMethod(method.id)} style={{ padding: '16px 12px', borderRadius: 'var(--r-lg)', border: paymentMethod === method.id ? '2px solid var(--brand)' : '1.5px solid var(--border)', background: paymentMethod === method.id ? 'var(--brand-lt)' : 'var(--surface)', cursor: 'pointer', transition: 'all .15s', textAlign: 'center', fontFamily: 'inherit' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: paymentMethod === method.id ? 'var(--brand)' : 'var(--ink2)' }}>{method.label}</div>
                  </button>
                ))}
              </div>
              {paymentMethod === 'mpesa' && (
                <div className="form-group">
                  <label className="form-label">M-Pesa Reference (optional)</label>
                  <input className="form-input" value={mpesaRef} onChange={(event) => setMpesaRef(event.target.value)} placeholder="e.g. QGX7XXXXXXX" />
                </div>
              )}
              <div style={{ background: 'var(--bg2)', borderRadius: 'var(--r-sm)', padding: '12px 14px', marginBottom: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--ink3)', marginBottom: 6 }}><span>Items</span><span>{itemCount}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700 }}><span>Total</span><span style={{ fontFamily: 'var(--mono)' }}>{fmt.currency(total)}</span></div>
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn btn-secondary" onClick={() => setShowPayModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handlePay} disabled={saleMutation.isPending}>{saleMutation.isPending ? 'Processing...' : `Confirm ${paymentMethod === 'mpesa' ? 'M-Pesa' : paymentMethod === 'card' ? 'Card' : paymentMethod === 'credit' ? 'Credit' : 'Cash'} Payment`}</button>
            </div>
          </div>
        </div>
      )}

      {showReceipt && (
        <div className="modal-overlay" onClick={(event) => event.target === event.currentTarget && setShowReceipt(null)}>
          <div className="modal scale-in" style={{ maxWidth: 560 }}>
            <div className="modal-head">
              <div>
                <div className="modal-title">Sale Complete</div>
                <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 4 }}>{fmt.datetime(showReceipt.created_at)}</div>
              </div>
              <button className="modal-close" onClick={() => setShowReceipt(null)}>x</button>
            </div>
            <div className="modal-body">
              <div style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)', color: '#fff', borderRadius: 20, padding: 20, marginBottom: 16 }}>
                <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.2em', opacity: 0.72 }}>Receipt Preview</div>
                <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.04em', marginTop: 6 }}>{settings.store_name || 'Your Store'}</div>
                <div style={{ fontSize: 13, opacity: 0.8, marginTop: 6 }}>{showReceipt.receipt_no}</div>
              </div>

              <div style={{ background: 'var(--bg)', borderRadius: 'var(--r)', padding: 16 }}>
                {(showReceipt.items || []).map((item) => (
                  <div key={`${item.id}-${item.name}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{item.name || item.product_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink3)' }}>{item.qty} x {fmt.currency(item.price)}</div>
                    </div>
                    <div className="mono">{fmt.currency(item.lineTotal || item.line_total)}</div>
                  </div>
                ))}
                <div style={{ marginTop: 14, display: 'grid', gap: 8 }}>
                  <div className="flex-between"><span>Subtotal</span><span className="mono">{fmt.currency(showReceipt.subtotal)}</span></div>
                  <div className="flex-between"><span>VAT</span><span className="mono">{fmt.currency(showReceipt.tax_amt)}</span></div>
                  {Number(showReceipt.discount_pct || 0) > 0 && <div className="flex-between"><span>Discount</span><span className="mono">- {fmt.currency(showReceipt.discount_amt)}</span></div>}
                  <div className="flex-between" style={{ fontWeight: 700 }}><span>Total</span><span className="mono">{fmt.currency(showReceipt.total)}</span></div>
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn btn-secondary" onClick={() => setShowReceipt(null)}>Close</button>
              <button className="btn btn-secondary" onClick={previewReceipt}>Preview / Save PDF</button>
              <button className="btn btn-primary" onClick={() => printMutation.mutate(showReceipt)} disabled={printMutation.isPending}>Queue Print</button>
            </div>
          </div>
        </div>
      )}

      <CameraScannerModal open={scannerOpen} onClose={() => setScannerOpen(false)} onDetected={handleBarcode} />
    </div>
  );
}

function QtyBtn({ children, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ width: 22, height: 22, borderRadius: '50%', border: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: 'var(--ink2)', cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all .1s', background: 'none', flexShrink: 0, opacity: disabled ? 0.4 : 1 }}>
      {children}
    </button>
  );
}

function TotalRow({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 7, color: color || 'var(--ink2)' }}>
      <span>{label}</span><span style={{ fontFamily: 'var(--mono)' }}>{value}</span>
    </div>
  );
}
